import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/site/Reveal";
import LaunchingSoonCard from "@/components/site/LaunchingSoonCard";

const IMAGES = {
  pharma: [
    "https://images.pexels.com/photos/13779111/pexels-photo-13779111.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/15897784/pexels-photo-15897784.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  ],
  skin: [
    "https://images.pexels.com/photos/16378451/pexels-photo-16378451.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.pexels.com/photos/7670676/pexels-photo-7670676.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  ],
};

function BrandColumn({ id, brand, kicker, title, blurb, tone, onCta, ctaLabel }) {
  const isSkin = tone === "skin";
  const label = isSkin ? "One Skin" : "OneKind";
  return (
    <div
      id={id}
      data-testid={`brand-column-${brand}`}
      className={`p-8 md:p-14 lg:p-20 flex flex-col gap-10 group scroll-mt-24 ${
        isSkin ? "" : "border-b lg:border-b-0 lg:border-r border-ink/10"
      }`}
    >
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-3">
          <span className={`w-2 h-2 rounded-full ${isSkin ? "bg-derma" : "bg-pharma"}`} />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">{kicker}</span>
        </div>
        <h3 className="font-serif text-5xl md:text-6xl leading-[0.9] tracking-tight">
          <MaskedLines lines={[title]} />
        </h3>
        <Reveal delay={0.1}>
          <p className="font-sans text-base text-ink/65 leading-relaxed max-w-md">{blurb}</p>
        </Reveal>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {IMAGES[tone].map((img, i) => (
          <LaunchingSoonCard key={i} image={img} tone={tone} index={i} label={label} />
        ))}
      </div>

      <button
        onClick={onCta}
        data-testid={`brand-cta-${brand}`}
        className={`self-start flex items-center gap-2 rounded-full px-7 py-3.5 text-xs uppercase tracking-widest font-sans text-stone transition-colors ${
          isSkin ? "bg-derma hover:bg-derma-dark" : "bg-pharma hover:bg-pharma-dark"
        }`}
      >
        {ctaLabel}
        <ArrowUpRight size={15} />
      </button>
    </div>
  );
}

export default function DualBrand() {
  const navigate = useNavigate();

  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 border-b border-ink/10">
      <BrandColumn
        id="onekind"
        brand="onekind"
        tone="pharma"
        kicker="OneKind · Pharmaceuticals & Wellness"
        title="Oral medications, engineered for outcomes."
        blurb="From clinically-dosed oral therapeutics to daily wellness formulations, OneKind is built for the science your patients can rely on."
        ctaLabel="Explore OneKind"
        onCta={() => navigate("/shop?brand=onekind")}
      />
      <BrandColumn
        id="oneskin"
        brand="oneskin"
        tone="skin"
        kicker="One Skin · Dermatological Skincare"
        title="Dermatological grade, dermatologist trusted."
        blurb="One Skin delivers active-led skincare — barrier repair, retinal renewal and mineral protection — formulated with clinical actives, and sold through inoneskin.com."
        ctaLabel="Explore One Skin"
        onCta={() => navigate("/shop?brand=oneskin")}
      />
    </section>
  );
}
